import { createRouter, createWebHistory } from 'vue-router'
import AppLayout from '@/components/layout/AppLayout.vue'
import ResourcePage from '@/components/crud/ResourcePage.vue'
import { session } from '@/stores/session'
import { firstAccessiblePath } from '@/config/navigation'
import { vehicleTypesConfig, vehiclesConfig } from '@/config/resources'

// Le menu et les routes sont pilotés par les permissions de /auth/me
// (`meta.permission`), jamais par le nom du rôle. Le serveur reste la
// référence : une route ouverte ici répond 403 s'il le faut.
const routes = [
  { path: '/login', name: 'login', component: () => import('@/views/auth/LoginView.vue'), meta: { guest: true, title: 'Connexion' } },
  { path: '/register', name: 'register', component: () => import('@/views/auth/RegisterView.vue'), meta: { guest: true, title: 'Créer une organisation' } },
  { path: '/forgot-password', name: 'forgot-password', component: () => import('@/views/auth/ForgotPasswordView.vue'), meta: { guest: true, title: 'Mot de passe oublié' } },
  // Lien envoyé par e-mail : /reset-password?token=...&email=...
  { path: '/reset-password', name: 'reset-password', component: () => import('@/views/auth/ResetPasswordView.vue'), meta: { public: true, title: 'Nouveau mot de passe' } },

  {
    path: '/',
    component: AppLayout,
    children: [
      { path: '', name: 'home', component: { render: () => null } },
      { path: 'dashboard', name: 'dashboard', component: () => import('@/views/DashboardView.vue'), meta: { permission: 'dashboard.view', title: 'Tableau de bord' } },

      { path: 'vehicles', name: 'vehicles', component: ResourcePage, props: { config: vehiclesConfig }, meta: { permission: 'vehicles.view', title: 'Véhicules' } },
      { path: 'vehicles/:id(\\d+)', name: 'vehicle', component: () => import('@/views/fleet/VehicleDetailView.vue'), props: true, meta: { permission: 'vehicles.view', title: 'Véhicule' } },
      { path: 'drivers', name: 'drivers', component: () => import('@/views/fleet/DriversView.vue'), meta: { permission: 'drivers.view', title: 'Chauffeurs' } },
      { path: 'drivers/:id(\\d+)', name: 'driver', component: () => import('@/views/fleet/DriverDetailView.vue'), props: true, meta: { permission: 'drivers.view', title: 'Chauffeur' } },
      { path: 'owners', name: 'owners', component: () => import('@/views/fleet/OwnersView.vue'), meta: { permission: 'owners.view', title: 'Propriétaires' } },
      { path: 'vehicle-types', name: 'vehicle-types', component: ResourcePage, props: { config: vehicleTypesConfig }, meta: { permission: 'vehicle_types.view', title: 'Types de véhicules' } },

      { path: 'revenues', name: 'revenues', component: () => import('@/views/finance/RevenuesView.vue'), meta: { permission: 'revenues.view', title: 'Recettes' } },
      { path: 'revenues/summary', name: 'revenues-summary', component: () => import('@/views/finance/QuotaReportView.vue'), props: { mode: 'revenues' }, meta: { permission: 'revenues.view', title: 'Recettes et quota' } },
      { path: 'expenses', name: 'expenses', component: () => import('@/views/finance/ExpensesView.vue'), meta: { permission: 'expenses.view', title: 'Dépenses' } },
      { path: 'expenses/categories', name: 'expense-categories', component: () => import('@/views/finance/ExpenseCategoriesView.vue'), meta: { permission: 'expense_categories.view', title: 'Catégories de dépenses' } },
      { path: 'payments', name: 'payments', component: () => import('@/views/finance/PaymentsView.vue'), meta: { permission: 'payments.view', title: 'Versements' } },
      { path: 'payments/calendar', name: 'payments-calendar', component: () => import('@/views/finance/PaymentsCalendarView.vue'), meta: { permission: 'payments.view', title: 'Calendrier des versements' } },
      { path: 'reconciliation', name: 'reconciliation', component: () => import('@/views/finance/QuotaReportView.vue'), props: { mode: 'reconciliation' }, meta: { permission: 'reconciliation.view', title: 'Rapprochement' } },
      // Permission « salaries.view » non confirmée par le brief : voir config/navigation.js.
      { path: 'salaries', name: 'salaries', component: () => import('@/views/finance/SalariesView.vue'), meta: { permission: 'revenues.view', title: 'Salaires' } },

      { path: 'fuel', name: 'fuel', component: () => import('@/views/fuel/FuelView.vue'), meta: { permission: 'fuel_logs.view', title: 'Carburant' } },
      { path: 'fuel/consumption', name: 'fuel-consumption', component: () => import('@/views/fuel/FuelConsumptionView.vue'), meta: { permission: 'fuel_logs.view', title: 'Consommation' } },

      { path: 'maintenance', name: 'maintenance', component: () => import('@/views/maintenance/MaintenanceDueView.vue'), meta: { permission: 'maintenance_records.view', title: 'Maintenance' } },
      { path: 'maintenance/records', name: 'maintenance-records', component: () => import('@/views/maintenance/MaintenanceListView.vue'), props: { kind: 'records' }, meta: { permission: 'maintenance_records.view', title: 'Interventions' } },
      { path: 'maintenance/plans', name: 'maintenance-plans', component: () => import('@/views/maintenance/MaintenanceListView.vue'), props: { kind: 'plans' }, meta: { permission: 'maintenance_plans.view', title: 'Plans de maintenance' } },

      { path: 'reports', name: 'reports', component: () => import('@/views/reports/ReportsView.vue'), meta: { permission: 'reports.view', title: 'Rapports' } },
      { path: 'notifications', name: 'notifications', component: () => import('@/views/account/NotificationsView.vue'), meta: { title: 'Notifications' } },
      { path: 'profile', name: 'profile', component: () => import('@/views/account/ProfileView.vue'), meta: { title: 'Mon profil' } },

      { path: 'users', name: 'users', component: () => import('@/views/admin/UsersView.vue'), meta: { permission: 'users.view', title: 'Utilisateurs' } },
      { path: 'roles', name: 'roles', component: () => import('@/views/admin/RolesView.vue'), meta: { permission: 'roles.view', title: 'Rôles' } },
      { path: 'audit', name: 'audit', component: () => import('@/views/admin/AuditView.vue'), meta: { permission: 'audit.view', title: "Journal d'audit" } },

      { path: 'forbidden', name: 'forbidden', component: () => import('@/views/ErrorPageView.vue'), props: { kind: 'forbidden' }, meta: { title: 'Accès non autorisé' } },
      { path: ':pathMatch(.*)*', name: 'not-found', component: () => import('@/views/ErrorPageView.vue'), props: { kind: 'not-found' }, meta: { title: 'Introuvable' } }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 })
})

router.beforeEach(async (to) => {
  await session.restore()

  if (to.meta.public) return true
  if (to.meta.guest) return session.isAuthenticated ? { name: 'home' } : true
  if (!session.isAuthenticated) {
    const query = to.fullPath && to.fullPath !== '/' ? { redirect: to.fullPath } : {}
    if (session.sessionExpired) {
      query.expired = '1'
      session.sessionExpired = false
    }
    return { name: 'login', query }
  }
  if (to.name === 'home') return firstAccessiblePath((p) => session.can(p))
  if (to.meta.permission && !session.can(to.meta.permission)) return { name: 'forbidden' }
  return true
})

router.afterEach((to) => {
  document.title = to.meta.title ? `${to.meta.title} · GesRecette` : 'GesRecette'
})

export default router
